<section class="hero-section" role="banner">
  <div class="hero-background-elements" aria-hidden="true">
    <div class="bg-element element-1"></div>
    <div class="bg-element element-2"></div>
    <div class="bg-element element-3"></div>
  </div>

  <div class="container">
    <div class="row align-items-center">
      <div class="col-lg-7">
        <div class="hero-content">
          <span class="hero-badge">Enterprise Object Storage Platform</span>

          <h1 class="hero-title">
            Modern <span class="text-highlight">Object Storage</span> for Cloud-Native Workloads
          </h1>

          <p class="hero-description">
            Secure, S3-compatible object storage designed for web applications, media, analytics, and long-term archives with predictable performance and transparent pricing.
          </p>

          <div class="hero-actions">
            <a routerLink="/contact" class="btn-primary-gradient">
              <i class="bx bx-rocket" aria-hidden="true"></i>
              Start a Project
            </a>
            <a routerLink="/pricing" class="btn-outline-light">
              <i class="bx bx-dollar" aria-hidden="true"></i>
              Explore Pricing
            </a>
          </div>

          <div class="hero-stats">
            <div class="stat-item">
              <div class="stat-number">11x9</div>
              <div class="stat-label">Durability</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">S3</div>
              <div class="stat-label">API Compatible</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">PB+</div>
              <div class="stat-label">Scalability</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">&lt;50ms</div>
              <div class="stat-label">Typical Latency</div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-5">
        <div class="hero-visual">
          <div class="hero-visual-card">
            <div class="hero-visual-header">
              <span class="hero-visual-title">Object Storage Overview</span>
              <span class="hero-visual-tag">Live</span>
            </div>
            <div class="hero-visual-body">
              <div class="hero-visual-metric">
                <div class="metric-label">Stored Objects</div>
                <div class="metric-value">3.2B</div>
              </div>
              <div class="hero-visual-metric">
                <div class="metric-label">Daily Requests</div>
                <div class="metric-value">1.4M</div>
              </div>
              <div class="hero-visual-metric">
                <div class="metric-label">Regions</div>
                <div class="metric-value">6</div>
              </div>
            </div>
            <div class="hero-visual-footer">
              <div class="hero-visual-pill">Encryption: AES‑256</div>
              <div class="hero-visual-pill">Redundancy: Multi‑AZ</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<section class="storage-types-section ptb-100">
    <div class="container">
        <div class="section-title text-center">
            <span class="section-badge">Object Storage Options</span>
            <h2>Choose Your Storage Class</h2>
            <p>Select from our comprehensive range of object storage classes designed for different access patterns, from frequently accessed data to long-term archival storage.</p>
        </div>

        <div class="row">
            <div class="col-lg-4 col-md-6 mb-4">
                <div class="storage-type-card featured">
                    <div class="storage-header">
                        <div class="storage-icon">
                            <i class="bx bx-cloud" aria-hidden="true"></i>
                        </div>
                        <div class="storage-badge">Popular</div>
                    </div>
                    <div class="storage-content">
                        <h3>Standard Storage</h3>
                        <p class="storage-price">Best for backups and less frequently accessed data.</p>
                        <p class="storage-description">High-performance object storage for frequently accessed data with low-latency access and high throughput.</p>
                        
                        <div class="storage-features">
                            <h5>Key Features:</h5>
                            <ul class="feature-list">
                                <li><i class="bx bx-check" aria-hidden="true"></i> 99.99% durability</li>
                                <li><i class="bx bx-check" aria-hidden="true"></i> S3-compatible API</li>
                                <li><i class="bx bx-check" aria-hidden="true"></i> CDN integration</li>
                                <li><i class="bx bx-check" aria-hidden="true"></i> Lifecycle management</li>
                            </ul>
                        </div>
                        
                        <div class="storage-specs">
                            <div class="spec-grid">
                                <div class="spec-item">
                                    <span class="spec-label">Capacity:</span>
                                    <span class="spec-value">Unlimited</span>
                                </div>
                                <div class="spec-item">
                                    <span class="spec-label">Availability:</span>
                                    <span class="spec-value">99.99%</span>
                                </div>
                            </div>
                        </div>
                        
                        <div class="storage-footer">
                            <a routerLink="/contact" class="btn-select-storage">Contact Us</a>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-lg-4 col-md-6 mb-4">
                <div class="storage-type-card">
                    <div class="storage-header">
                        <div class="storage-icon">
                            <i class="bx bx-time" aria-hidden="true"></i>
                        </div>
                        <div class="storage-badge scalable">Infrequent</div>
                    </div>
                    <div class="storage-content">
                        <h3>Infrequent Access</h3>
                        <p class="storage-price">Optimized for compliance and long-term retention.</p>
                        <p class="storage-description">Lower-cost storage for data that is accessed less frequently but requires rapid access when needed.</p>
                        
                        <div class="storage-features">
                            <h5>Key Features:</h5>
                            <ul class="feature-list">
                                <li><i class="bx bx-check" aria-hidden="true"></i> 99.99% durability</li>
                                <li><i class="bx bx-check" aria-hidden="true"></i> S3-compatible API</li>
                                <li><i class="bx bx-check" aria-hidden="true"></i> Lifecycle automation</li>
                                <li><i class="bx bx-check" aria-hidden="true"></i> 10-20ms latency</li>
                            </ul>
                        </div>
                        
                        <div class="storage-specs">
                            <div class="spec-grid">
                                <div class="spec-item">
                                    <span class="spec-label">Minimum Duration:</span>
                                    <span class="spec-value">30 days</span>
                                </div>
                                <div class="spec-item">
                                    <span class="spec-label">Availability:</span>
                                    <span class="spec-value">99.9%</span>
                                </div>
                            </div>
                        </div>
                        
                        <div class="storage-footer">
                            <a routerLink="/contact" class="btn-select-storage outline">Contact Us</a>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-lg-4 col-md-6 mb-4">
                <div class="storage-type-card">
                    <div class="storage-header">
                        <div class="storage-icon">
                            <i class="bx bx-archive" aria-hidden="true"></i>
                        </div>
                        <div class="storage-badge archive">Archive</div>
                    </div>
                    <div class="storage-content">
                        <h3>Archive Storage</h3>
                        <p class="storage-price">Ideal for hot content, APIs, and user-facing workloads.</p>
                        <p class="storage-description">Lowest-cost storage for long-term data archiving with longer retrieval times but highest cost savings.</p>
                        
                        <div class="storage-features">
                            <h5>Key Features:</h5>
                            <ul class="feature-list">
                                <li><i class="bx bx-check" aria-hidden="true"></i> 99.99% durability</li>
                                <li><i class="bx bx-check" aria-hidden="true"></i> Compliance ready</li>
                                <li><i class="bx bx-check" aria-hidden="true"></i> Automated tiering</li>
                                <li><i class="bx bx-check" aria-hidden="true"></i> Long-term retention</li>
                            </ul>
                        </div>
                        
                        <div class="storage-specs">
                            <div class="spec-grid">
                                <div class="spec-item">
                                    <span class="spec-label">Retrieval:</span>
                                    <span class="spec-value">1-12 hours</span>
                                </div>
                                <div class="spec-item">
                                    <span class="spec-label">Retention:</span>
                                    <span class="spec-value">Unlimited</span>
                                </div>
                            </div>
                        </div>
                        
                        <div class="storage-footer">
                            <a routerLink="/contact" class="btn-select-storage outline">Contact Us</a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</section>

<section class="features-section ptb-100">
  <div class="container">
    <div class="section-title text-center">
      <span class="section-badge">Object Storage Features</span>
      <h2>Advanced <span class="text-highlight">Capabilities</span></h2>
      <p>Enterprise-grade capabilities built to protect data, simplify operations, and deliver consistent performance at any scale.</p>
    </div>
    
    <div class="row justify-content-center">
      <div class="col-lg-4 col-md-6 mb-4">
        <div class="feature-card">
          <div class="feature-icon">
            <i class="bx bx-shield-check" aria-hidden="true"></i>
          </div>
          <h3>Advanced Security</h3>
          <p>
            Enterprise-grade security with encryption at rest and in transit, access controls, and compliance certifications for data protection.
          </p>
          <ul class="feature-list">
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> AES-256 encryption</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> IAM integration</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Access control policies</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Audit logging</li>
          </ul>
        </div>
      </div>
      
      <div class="col-lg-4 col-md-6 mb-4">
        <div class="feature-card">
          <div class="feature-icon">
            <i class="bx bx-data" aria-hidden="true"></i>
          </div>
          <h3>Lifecycle Management</h3>
          <p>
            Automated lifecycle policies to transition data between storage classes based on age or custom rules for optimal cost efficiency.
          </p>
          <ul class="feature-list">
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Automated transitions</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Custom lifecycle rules</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Expiration policies</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Cross-region replication</li>
          </ul>
        </div>
      </div>
      
      <div class="col-lg-4 col-md-6 mb-4">
        <div class="feature-card">
          <div class="feature-icon">
            <i class="bx bx-network-chart" aria-hidden="true"></i>
          </div>
          <h3>Global Performance</h3>
          <p>
            Optimized for high throughput and low latency with global CDN integration and multi-region availability for fast content delivery.
          </p>
          <ul class="feature-list">
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Global CDN integration</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Multi-region availability</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Edge caching</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> High concurrency</li>
          </ul>
        </div>
      </div>
      
      <div class="col-lg-4 col-md-6 mb-4">
        <div class="feature-card">
          <div class="feature-icon">
            <i class="bx bx-code-alt" aria-hidden="true"></i>
          </div>
          <h3>S3-Compatible API</h3>
          <p>
            Full compatibility with Amazon S3 API for seamless migration and integration with existing tools and applications.
          </p>
          <ul class="feature-list">
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> RESTful APIs</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> S3 compatibility</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Multiple SDKs</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Third-party tools</li>
          </ul>
        </div>
      </div>
      
      <div class="col-lg-4 col-md-6 mb-4">
        <div class="feature-card">
          <div class="feature-icon">
            <i class="bx bx-analyse" aria-hidden="true"></i>
          </div>
          <h3>Storage Analytics</h3>
          <p>
            Comprehensive monitoring and analytics with usage insights, performance metrics, and cost optimization recommendations.
          </p>
          <ul class="feature-list">
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Usage analytics</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Performance monitoring</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Cost optimization</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Custom dashboards</li>
          </ul>
        </div>
      </div>
      
      <div class="col-lg-4 col-md-6 mb-4">
        <div class="feature-card">
          <div class="feature-icon">
            <i class="bx bx-sync" aria-hidden="true"></i>
          </div>
          <h3>Version Control</h3>
          <p>
            Automatic versioning to protect against accidental deletion or overwrites with point-in-time recovery capabilities.
          </p>
          <ul class="feature-list">
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Automatic versioning</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Point-in-time recovery</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Delete markers</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Cross-region sync</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</section>

<section class="use-cases-section ptb-100">
  <div class="container">
    <div class="section-title text-center">
      <span class="section-badge">Object Storage Use Cases</span>
      <h2>Common <span class="text-highlight">Applications</span></h2>
      <p>See how modern teams rely on object storage to power critical workloads across industries.</p>
    </div>
    
    <div class="row">
      <div class="col-lg-6 col-md-6 mb-4">
        <div class="use-case-card">
          <div class="use-case-icon">
            <i class="bx bx-world" aria-hidden="true"></i>
          </div>
          <h3>Web & Media Hosting</h3>
          <p>
            Host websites, streaming media, and static assets with global CDN integration for fast content delivery and optimal user experience.
          </p>
          <ul class="use-case-list">
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Global CDN integration</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Static website hosting</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Media streaming support</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Edge caching</li>
          </ul>
          <div class="use-case-stats">
            <div class="stat-badge">
              <span class="stat-value">< 50ms</span> Latency
            </div>
            <div class="stat-badge">
              <span class="stat-value">99.9%</span> Availability
            </div>
          </div>
        </div>
      </div>
      
      <div class="col-lg-6 col-md-6 mb-4">
        <div class="use-case-card">
          <div class="use-case-icon">
            <i class="bx bx-data" aria-hidden="true"></i>
          </div>
          <h3>Backup & Archive</h3>
          <p>
            Secure backup and long-term archival solutions with automated retention policies, compliance features, and disaster recovery capabilities.
          </p>
          <ul class="use-case-list">
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Automated backup schedules</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Compliance retention</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Disaster recovery</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Cross-region replication</li>
          </ul>
          <div class="use-case-stats">
            <div class="stat-badge">
              <span class="stat-value">99.99%</span> Durability
            </div>
            <div class="stat-badge">
              <span class="stat-value">24/7</span> Monitoring
            </div>
          </div>
        </div>
      </div>
      
      <div class="col-lg-6 col-md-6 mb-4">
        <div class="use-case-card">
          <div class="use-case-icon">
            <i class="bx bx-analyse" aria-hidden="true"></i>
          </div>
          <h3>Analytics & Processing</h3>
          <p>
            Store and process large datasets for analytics, machine learning, and business intelligence with high-performance compute integration.
          </p>
          <ul class="use-case-list">
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Petabyte-scale storage</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Compute integration</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Data lake architecture</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> ML/AI workflows</li>
          </ul>
          <div class="use-case-stats">
            <div class="stat-badge">
              <span class="stat-value">PB</span> Scale
            </div>
            <div class="stat-badge">
              <span class="stat-value">GPU</span> Compute
            </div>
          </div>
        </div>
      </div>
      
      <div class="col-lg-6 col-md-6 mb-4">
        <div class="use-case-card">
          <div class="use-case-icon">
            <i class="bx bx-mobile" aria-hidden="true"></i>
          </div>
          <h3>Mobile Applications</h3>
          <p>
            Scalable storage for mobile applications with high availability, global distribution, and seamless integration with mobile development frameworks.
          </p>
          <ul class="use-case-list">
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Mobile SDKs</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Global distribution</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> Push notifications</li>
            <li><i class="bx bx-check-circle" aria-hidden="true"></i> User data sync</li>
          </ul>
          <div class="use-case-stats">
            <div class="stat-badge">
              <span class="stat-value">Millions</span> Users
            </div>
            <div class="stat-badge">
              <span class="stat-value">99.9%</span> Uptime
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>



<section class="cta-section ptb-100" role="contentinfo">
  <div class="container">
    <div class="text-center">
      <h2 class="text-3xl md:text-4xl font-bold mb-6">
        Ready to Store Your Data?
      </h2>
      <p class="text-xl text-gray-300 mb-10 max-w-3xl mx-auto">
        Start with our scalable object storage solutions today. Get enterprise-grade features, 99.99% durability, and pay-as-you-use pricing with no upfront costs.
      </p>
      <div class="cta-actions">
        <a routerLink="/contact" class="btn-primary-gradient">
          Get Started Today
        </a>
        <a routerLink="/pricing" class="btn-outline">
          View Pricing
        </a>
      </div>
    </div>
  </div>
</section>
